import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ReactComponent as VerifiedIcon } from '../../assets/icons/verified.svg';
import { ReactComponent as MessageIcon } from '../../assets/icons/message.svg';
import { ReactComponent as InviteIcon } from '../../assets/icons/invite.svg';
import { ReactComponent as ChevronIcon } from '../../assets/icons/chevron.svg';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { getInfluencerById, getInfluencerFeed } from '../../store/slices/influencer/asyncThunks';
import {
  selectGetInfluencerFeedInfo,
  selectInfluencer,
  selectOpenMediaPreview,
  setOpenMediaPreview,
} from '../../store/slices/influencer/slice';
import {
  Avatar,
  BackButton,
  ButtonsContainer,
  Column,
  Container,
  Content,
  DetailsContent,
  EmptyMediaContainer,
  EmptyMediaLogo,
  EmptyMediaText,
  InfiniteScrollMarker,
  InfluencerInfoContainer,
  LoaderContainer,
  LoaderMoreContainer,
  MessageButton,
  NameText,
  Row,
  ShareButton,
  StoriesContainer,
  StoryContainer,
  TelegramUsername,
} from './InfluencerProfilePage.styles';
import Spinner from '../../components/Spinner/Spinner';
import { ContentSafeAreaView, Image, InfluencerStoriesSkeleton, MediaFeed, Video } from '../../components';
import { notifyError, notifyInfo } from '../../utils/notify';
import { register } from '../../store/slices/auth/asyncThunks';
import { selectStartParams } from '../../store/slices/auth/slice';
import { isTelegramWebApp, openTelegramLink, triggerHaptic } from '../../utils/telegram';
import { FEED_LIMIT } from '../ReelsPage/constants';
import { getEntityId, mapFeedItemsToReels } from '../../utils/feed';
import glassLogo from '../../assets/images/glass-logo.png';

const InfluencerProfilePage = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const influencer = useSelector(selectInfluencer);
  const startParams = useSelector(selectStartParams);
  const openMediaPreview = useSelector(selectOpenMediaPreview);
  const loadMoreRef = useRef();

  const { data, hasMore, nextCursor, loaded, pending, fetchMorePending, influencerId } =
    useSelector(selectGetInfluencerFeedInfo);
  const reels = useMemo(() => mapFeedItemsToReels(data), [data]);
  const currentInfluencerId = getEntityId(influencer);
  const hasCurrentInfluencer =
    influencer &&
    [currentInfluencerId, influencer.telegramUsername].filter(Boolean).some(value => String(value) === String(id));
  const hasLoadedCurrentInfluencerFeed =
    !currentInfluencerId || (loaded && String(influencerId) === String(currentInfluencerId));
  const showFeedLoading = pending || (hasCurrentInfluencer && !hasLoadedCurrentInfluencerFeed);

  const [startIndex, setStartIndex] = useState(0);

  const openFeed = index => {
    setStartIndex(index);
    dispatch(setOpenMediaPreview(true));
  };

  useEffect(() => {
    if (!hasCurrentInfluencer) {
      dispatch(getInfluencerById({ id }));
    }
  }, [dispatch, hasCurrentInfluencer, id]);

  useEffect(() => {
    if (hasCurrentInfluencer && currentInfluencerId && influencerId !== currentInfluencerId) {
      dispatch(getInfluencerFeed({ influencerId: currentInfluencerId, limit: FEED_LIMIT }))
        .unwrap()
        .catch(error => {
          notifyError(error.message || 'Unable to load influencer feed right now.');
        });
    }
  }, [currentInfluencerId, dispatch, hasCurrentInfluencer, influencerId]);

  const handleIntersection = useCallback(
    entries => {
      const [entry] = entries;
      if (
        entry.isIntersecting &&
        hasCurrentInfluencer &&
        currentInfluencerId &&
        !pending &&
        !fetchMorePending &&
        hasMore &&
        nextCursor
      ) {
        dispatch(getInfluencerFeed({ influencerId: currentInfluencerId, limit: FEED_LIMIT, cursor: nextCursor }))
          .unwrap()
          .catch(error => {
            notifyError(error.message || 'Unable to load more feed items right now.');
          });
      }
    },
    [currentInfluencerId, dispatch, fetchMorePending, hasCurrentInfluencer, hasMore, nextCursor, pending],
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, {
      root: null,
      threshold: 1.0,
    });
    const target = loadMoreRef.current;
    if (target) observer.observe(target);

    return () => {
      if (target) observer.unobserve(target);
    };
  }, [handleIntersection]);

  const onShareClick = () => {
    triggerHaptic();
    const link = `https://t.me/${influencer.telegramUsername}`;
    navigator.clipboard
      .writeText(link)
      .then(() => notifyInfo('Telegram link copied!'))
      .catch(err => {
        notifyError('Failed to copy link');
      });
  };

  const onMessageClick = () => {
    triggerHaptic();
    if (startParams.affiliateId) {
      dispatch(register(startParams));
    }
    openTelegramLink(`https://t.me/${influencer.telegramUsername}`);
  };

  return (
    <ContentSafeAreaView hideScrollbar>
      <Container>
        {!hasCurrentInfluencer ? (
          <LoaderContainer>
            <Spinner width={150} height={150} />
          </LoaderContainer>
        ) : (
          <Content>
            {!isTelegramWebApp() && (
              <BackButton onClick={() => navigate(-1)}>
                <ChevronIcon />
                Back
              </BackButton>
            )}
            <InfluencerInfoContainer>
              <Avatar $isSubscribed={influencer.isUserSubscribed} src={influencer.profilePhoto} />
              <DetailsContent>
                <Column>
                  <Row>
                    <NameText>{influencer.name}</NameText>
                    <VerifiedIcon />
                  </Row>
                  <TelegramUsername>@{influencer.telegramUsername}</TelegramUsername>
                </Column>
                <ButtonsContainer>
                  <MessageButton onClick={onMessageClick}>
                    <MessageIcon color="#000" />
                    message
                  </MessageButton>
                  <ShareButton onClick={onShareClick}>
                    <InviteIcon color="#fff" />
                    share
                  </ShareButton>
                </ButtonsContainer>
              </DetailsContent>
            </InfluencerInfoContainer>
            {showFeedLoading ? (
              <StoriesContainer>
                <InfluencerStoriesSkeleton />
              </StoriesContainer>
            ) : reels.length ? (
              <StoriesContainer>
                {reels.map((reel, index) => (
                  <StoryItem key={reel.id} media={reel.primaryMedia} name={reel.name} onClick={() => openFeed(index)} />
                ))}
                <InfiniteScrollMarker ref={loadMoreRef} />
              </StoriesContainer>
            ) : (
              <EmptyMediaContainer>
                <EmptyMediaLogo src={glassLogo} alt="" aria-hidden="true" />
                <EmptyMediaText>No posts yet</EmptyMediaText>
              </EmptyMediaContainer>
            )}
            {fetchMorePending && (
              <LoaderMoreContainer>
                <Spinner width={100} height={100} />
              </LoaderMoreContainer>
            )}
          </Content>
        )}
      </Container>
      {openMediaPreview && (
        <MediaFeed
          mediaList={data}
          startIndex={startIndex}
          onClose={() => dispatch(setOpenMediaPreview(false))}
          onLoadMore={() => {
            if (hasCurrentInfluencer && currentInfluencerId && !pending && !fetchMorePending && hasMore && nextCursor) {
              dispatch(getInfluencerFeed({ influencerId: currentInfluencerId, limit: FEED_LIMIT, cursor: nextCursor }))
                .unwrap()
                .catch(error => {
                  notifyError(error.message || 'Unable to load more feed items right now.');
                });
            }
          }}
          hasMore={hasMore}
          fetchMorePending={fetchMorePending}
        />
      )}
    </ContentSafeAreaView>
  );
};

const StoryItem = ({ media, name, onClick }) => {
  if (!media?.url && !media?.thumbnailUrl && !media?.previewUrl) {
    return null;
  }

  const isVideo = media.type === 'video';
  const thumbnailUrl = media.thumbnailUrl || media.previewUrl;
  const shouldRenderVideoFallback = isVideo && !thumbnailUrl && media.url;

  return (
    <StoryContainer onClick={onClick}>
      {shouldRenderVideoFallback ? (
        <Video src={media.url} autoPlay={false} playWhenVisible={false} preload="metadata" />
      ) : (
        <Image src={thumbnailUrl || media.url} alt={name} />
      )}
    </StoryContainer>
  );
};

export default InfluencerProfilePage;
